// ENG-3868 §3 / ENG-3942 — renderer post-Connected LiveKit startup probe
// scheduler. Per-app-launch, cloud-only, deferred 5s after Connected so it
// never contends with the local-bridge socket during icon-launch sessions.
// See ai.knowledge: eng-3942-regression-mechanism.

import { RoomEvent } from 'livekit-client';
import { runLiveKitConnectionDiagnostics } from './connection-diagnostics.js';

// Spec §6.4 — guard key. We deliberately do NOT include an appLaunchId
// suffix: sessionStorage in the renderer is already scoped to one
// BrowserWindow launch (it persists across window.location.reload() and
// clears on window close), which IS the per-app-launch semantics ENG-3942
// asks for. Knowledge: persistencelayer-vs-sessionstorage.
export const STARTUP_PROBE_GUARD_KEY = 'livekit.startupProbeFired';

// Spec §6.3 — deferred fire window.
export const STARTUP_PROBE_DELAY_MS = 5_000;

export type StartupProbeMode = 'local' | 'cloud';

export type StartupProbeStorage = Pick<Storage, 'getItem' | 'setItem'>;

export type StartupProbeFire = (opts: {
  url: string;
  token: string;
  mode: 'cloud';
  trigger: 'startup';
  roomId?: string;
}) => Promise<unknown> | void;

export type StartupProbeTimer = {
  setTimeout: (handler: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

// Minimal RoomEvent-emitting surface — accepts both real
// livekit-client Room and the test fake.
export type StartupProbeRoom = {
  on(event: RoomEvent, listener: (...args: unknown[]) => void): unknown;
  off?(event: RoomEvent, listener: (...args: unknown[]) => void): unknown;
};

export type StartupProbeOptions = {
  room: StartupProbeRoom;
  mode: StartupProbeMode;
  url: string;
  token: string;
  roomId?: string;
  storage?: StartupProbeStorage | null;
  timer?: StartupProbeTimer;
  // Allow tests to inject a spy; production wires the real diagnostics fn.
  fire?: StartupProbeFire;
};

const defaultTimer: StartupProbeTimer = {
  setTimeout: (handler, ms) => globalThis.setTimeout(handler, ms),
  clearTimeout: (handle) =>
    globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>)
};

const defaultFire: StartupProbeFire = (opts) =>
  runLiveKitConnectionDiagnostics({
    url: opts.url,
    token: opts.token,
    mode: opts.mode,
    trigger: opts.trigger,
    ...(opts.roomId ? { roomId: opts.roomId } : {})
  }).catch(() => {
    // C7 — diagnostic failures never affect the LiveKit path.
  });

/**
 * LiveKitStartupProbeScheduler — wires post-Connected fire-once probe.
 *
 * Per spec §6.3 / §6.4 / §6.5 / §6.6:
 *  - C1: never fires when mode === 'local'.
 *  - C2: never fires from LiveKitImpl's constructor.
 *  - C3: ≥ 5s after Connected, with the connection still Connected.
 *  - C4: persistent guard survives window.location.reload().
 *  - C6: every ConnectionCheck disposed (delegated to the diagnostics fn).
 *  - C7: diagnostic failures never affect the LiveKit connection path.
 */
export class LiveKitStartupProbeScheduler {
  readonly #opts: {
    room: StartupProbeRoom;
    mode: StartupProbeMode;
    url: string;
    token: string;
    roomId: string | undefined;
    storage: StartupProbeStorage | null;
    timer: StartupProbeTimer;
    fire: StartupProbeFire;
  };

  #pendingTimer: unknown = undefined;
  #attached = false;

  public constructor(opts: StartupProbeOptions) {
    this.#opts = {
      room: opts.room,
      mode: opts.mode,
      url: opts.url,
      token: opts.token,
      roomId: opts.roomId,
      // storage: caller may pass null to opt out (e.g. SSR); undefined =>
      // default to globalThis.sessionStorage if available.
      storage: opts.storage === undefined ? safeSessionStorage() : opts.storage,
      timer: opts.timer ?? defaultTimer,
      fire: opts.fire ?? defaultFire
    };
  }

  /** Cancel pending work and detach lifecycle listeners. */
  public dispose(): void {
    this.#onCancel();
    if (!this.#attached) return;

    this.#attached = false;
    if (this.#opts.mode !== 'cloud') return;

    this.#opts.room.off?.(RoomEvent.Connected, this.#onConnected);
    this.#opts.room.off?.(RoomEvent.Disconnected, this.#onCancel);
    this.#opts.room.off?.(RoomEvent.Reconnecting, this.#onCancel);
  }

  /** Subscribe to Room lifecycle events. Idempotent. */
  public attach(): void {
    if (this.#attached) return;
    this.#attached = true;

    // C1 — short-circuit in local mode. We still attach a no-op so the
    // method signature stays uniform, but skip the actual handlers.
    if (this.#opts.mode !== 'cloud') return;

    this.#opts.room.on(RoomEvent.Connected, this.#onConnected);
    this.#opts.room.on(RoomEvent.Disconnected, this.#onCancel);
    this.#opts.room.on(RoomEvent.Reconnecting, this.#onCancel);
  }

  #onConnected = (): void => {
    // C3 — schedule deferred fire; do not cancel any prior schedule because
    // a re-Connected after the timer elapsed is intentionally a no-op (we
    // gate inside the fire path on the per-app-launch guard).
    if (this.#pendingTimer !== undefined) return;
    this.#pendingTimer = this.#opts.timer.setTimeout(
      this.#fireIfStillEligible,
      STARTUP_PROBE_DELAY_MS
    );
  };

  #onCancel = (): void => {
    // C3 — cancel any pending fire if the connection transitions out of
    // Connected before the 5s gate elapses.
    if (this.#pendingTimer !== undefined) {
      this.#opts.timer.clearTimeout(this.#pendingTimer);
      this.#pendingTimer = undefined;
    }
  };

  #fireIfStillEligible = (): void => {
    this.#pendingTimer = undefined;

    // C4 / fail-closed — read+write guard via sessionStorage. If anything
    // throws (storage unavailable, quota, corrupt), treat as "already
    // fired" and skip. Spec §11.
    let alreadyFired = true;
    try {
      const storage = this.#opts.storage;
      if (storage) {
        alreadyFired = storage.getItem(STARTUP_PROBE_GUARD_KEY) !== null;
      } else {
        // No storage available → fail closed.
        alreadyFired = true;
      }
    } catch {
      alreadyFired = true;
    }
    if (alreadyFired) return;

    // C2 / re-entrancy — mark fired BEFORE invoking diagnostics so a second
    // Connected→timer fire (e.g. Reconnected after a brief drop) cannot
    // race a still-pending async diagnostics call.
    try {
      this.#opts.storage?.setItem(STARTUP_PROBE_GUARD_KEY, String(Date.now()));
    } catch {
      // Write failed; without a durable guard we are unsafe — fail closed.
      return;
    }

    // C7 — fire wraps the diagnostics call and swallows errors so LiveKit
    // path stays unaffected.
    this.#opts.fire({
      url: this.#opts.url,
      token: this.#opts.token,
      mode: 'cloud',
      trigger: 'startup',
      ...(this.#opts.roomId ? { roomId: this.#opts.roomId } : {})
    });
  };
}

function safeSessionStorage(): StartupProbeStorage | null {
  try {
    if (typeof globalThis === 'undefined') return null;
    const s = (globalThis as { sessionStorage?: Storage }).sessionStorage;
    if (!s) return null;
    return s;
  } catch {
    // Some SSR / sandboxed contexts throw on sessionStorage access.
    return null;
  }
}
